import { useParams } from "react-router";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import { ISubscriptionModalProps } from "./types";
import { EComModal } from "../../../../components/EComModal";
import { RootState } from "../../../../app/store";
import { customerSelectors } from "../../customerSlice";
import {
  BillingAddress,
  getCountryName,
  getPaymentMethodTextByPaymentMethod,
} from "../../../../types/model/paymentMethod";
import { EMPTY } from "../../../../constants/common";
import ModalContent from "../../../../components/EComModal/ModalContent";
import ModalListItem from "../../../../components/EComModal/ModalListItem";
import { Country } from "../../../../types/model/price";
import { fetchAllCountries } from "../../../../services/basePrice";
import { fetchRestartSubscription, restartSubscription } from "../../../../services/subscription";
import { RestartSubscriptionModel } from "../../../../types/model/subscriptions";

export default function RestartSubscriptionModal({
  subscription,
  visible,
  closeModal,
  onFinish,
}: ISubscriptionModalProps) {
  const [isFetching, setIsFetching] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [restartSubscriptionInfo, setRestartProductVariantInfo] = useState({} as RestartSubscriptionModel);
  const { customerId } = useParams<{ customerId: string }>();
  const customer = useSelector((state: RootState) => customerSelectors.selectById(state, customerId));

  const [countries, setCountries] = useState([] as Country[]);
  useEffect(() => {
    fetchAllCountries().then((data) => setCountries(data));
  }, []);

  useEffect(() => {
    if (!visible) {
      return;
    }
    fetchRestartSubscriptionInfo();
  }, [visible]);

  async function fetchRestartSubscriptionInfo() {
    setIsFetching(true);
    try {
      const restartSubscriptionInfo = await fetchRestartSubscription(subscription.subscriptionId);
      setRestartProductVariantInfo(restartSubscriptionInfo);
    } catch (e) {
      message.error(`Product not found. Please make a new order.`);
    } finally {
      setIsFetching(false);
    }
  }

  async function onSubmit() {
    setIsLoading(true);
    try {
      await restartSubscription(subscription.subscriptionId);
      message.success("Restart successful");
      if (onFinish) {
        onFinish();
      }
      closeModal();
    } finally {
      setIsLoading(false);
    }
  }

  const { planTitle, billingPlanName, symbol, amount, tax, total, paymentMethod } = restartSubscriptionInfo;
  const getBillingAddressText = (billingAddress?: BillingAddress): string =>
    billingAddress
      ? (billingAddress.district ? `${billingAddress.district}, ` : "") +
        `${billingAddress.postalCode}, ${getCountryName(billingAddress.country, countries)}`
      : EMPTY;

  return (
    <EComModal
      title="Restart subscription"
      visible={visible}
      okText="Restart"
      cancelText="Cancel"
      onCancel={closeModal}
      onOk={onSubmit}
      confirmLoading={isLoading}
    >
      <Spin size="large" spinning={isFetching}>
        <div>
          <p>Please verify the information below with the user. A new subscription will be created</p>
          <ModalContent>
            <ModalListItem label="Plan name:" value={planTitle} />
            <ModalListItem label="Billing plan:" value={billingPlanName} />
            <ModalListItem label="Customer name:" value={customer?.name || ""} />
            <ModalListItem
              label="Payment information:"
              value={paymentMethod ? getPaymentMethodTextByPaymentMethod(paymentMethod) : EMPTY}
            />
            <ModalListItem
              label="Billing address:"
              value={paymentMethod ? getBillingAddressText(paymentMethod.billingAddress) : EMPTY}
            />
            <ModalListItem label="Price:" value={`${symbol}${amount?.toFixed(2)}`} />
            <ModalListItem label="Estimated Tax:" value={`${symbol}${tax?.toFixed(2)}`} />
            <ModalListItem label="Today’s Total:" value={`${symbol}${total?.toFixed(2)}`} />
          </ModalContent>
        </div>
      </Spin>
    </EComModal>
  );
}
